import React, { useEffect, useMemo, useRef, useState } from 'react'
import { DateTime } from 'luxon'
import { useMutation, useQuery } from 'react-query'
import { useRouter } from 'next/router'

import { BASE_URL } from '@configs/config'
import { useTheme } from '@hooks/contexts/ThemeContext/ThemeContext'
import { useGetBookShelfStatus } from '@hooks/myReading/useGetBookShelfStatus'
import { useAuthentication } from '@hooks/useAuthentication'
import { useModal } from '@hooks/contexts/ModalContext/ModalContext'
import { useContentLevel } from '@hooks/useContentLevel'
import { UserActionLogType } from '@models/userActionLog/UserActionLogType'
import { UserActionEnum } from '@interfaces/UserActionEnum'
import { ContentLevelEnum } from '@interfaces/ContentLevelEnum'
import { rateRIds } from '@lib/utils'
import { useUserReadingAction } from '@hooks/userReading/useUserReadingAction'
import { useChapterAction } from '@hooks/chapter/useChapterAction'
import { WriterStatusEnum } from '@interfaces/WriterStatusEnum'
import { ChapterStatusEnum } from '@interfaces/ChapterStatusEnum'
import { BookStatusEnum } from '@interfaces/BookStatusEnum'
import { useBookmarkAction } from '@hooks/bookmark/useBookmarkAction'
import { useUserActionLogAction } from '@hooks/userActionLog/useUserActionLogAction'
import { BookChapterPreviewType } from '@models/chapter/BookChapterPreviewType'
import type {
  WithChapterPageProps,
  ChapterPageProps,
  ReadingPageFooterType,
} from './interface'
import { PromotionReadFreeEnum } from '@interfaces/PromotionTypeEnum'
import { useReadFreePromotion } from '@hooks/chapter/useReadFreePromotion'
import { gtagBeginCheckout } from '@lib/gtag'
import { fbqBeginCheckout } from '@lib/facebookPixel'
import { ttqBeginCheckout } from '@lib/tiktok'

const hideCloseModal = false

export function withChapterPage(Component: React.FC<ChapterPageProps>) {
  function WithChapterPage({
    bookId,
    chapterId,
    metaData,
  }: WithChapterPageProps) {
    const isIncrementView = true
    const userActionLogClient = useUserActionLogAction()
    const bookmarkClient = useBookmarkAction()
    const router = useRouter()
    const userReadingClient = useUserReadingAction()
    const chapterClient = useChapterAction()
    const {
      theme,
      fontSize,
      lineHeight,
      fontFamily,
      setThemeFromLocalStorage,
      setInitialData,
    } = useTheme()
    const { canFetchApi, isAuthenticated, user, userCoin } = useAuthentication()
    const {
      loginAndVerifyAgeCase,
      waitVerifyAgeCase,
      verifyAgeCase,
      checkBookRate18Plus,
    } = useContentLevel({ isIncrementView })
    const [readingPageFooterdata, setReadingPageFooterDate] =
      useState<ReadingPageFooterType>()
    const [showData, setShowData] = useState(false)
    const [disableWayPoint, setDisableWayPoint] = useState(false)
    const canCreateUserActionLog = useRef(false)
    const startTime = useRef<DateTime | null>(null)
    const previousChapterId = useRef<number | null>(null)
    previousChapterId.current !== null
    const notEnoughReadFreeQuotaModal = useModal({
      modal: 'notEnoughReadFreeQuota',
      handleOnClickClose: () => {
        router.replace(`/story/${bookId}`)
      },
      modalProps: {
        quota: 0,
        notEnoughCoin: false,
      },
    })
    const loginAndGoBack = useModal({
      modal: 'login',
      handleOnClickClose: () => {
        router.replace(`/story/${bookId}`)
      },
    })
    const notEnoughCoinModal = useModal({
      modal: 'notEnoughCoin',
      modalProps: {
        describeText: 'เนื้อเรื่องที่คุณเลือกอ่านติดเหรียญอยู่',
        purposeText: 'เข้าถึงเนื้อหานี้',
      },
      handleOnClickClose: () => {
        router.replace(`/story/${bookId}`)
      },
    })
    const buyChapterModal = useModal(
      {
        modal: 'buyChapter',
        handleOnClickClose: () => {
          router.replace(`/story/${bookId}`)
        },
      },
      true
    )
    const restrictModal = useModal(
      {
        modal: 'restrictModal',
        handleOnClickClose: () => {
          router.replace(`/story/${bookId}`)
        },
      },
      hideCloseModal
    )
    const changeEmailModal = useModal(
      {
        modal: 'changeEmail',
      },
      true
    )

    const { getTodayReadCount } = useReadFreePromotion(undefined)
    const { data, isFetching, refetch } = useQuery(
      ['story', bookId, 'chapter', chapterId],
      async () => {
        return chapterClient.bookChapterReader(chapterId).then(res => {
          const myBook = res.book.penName.user.id === user.id
          const checkIsPublish =
            res.status === ChapterStatusEnum.PUBLISHED &&
            DateTime.fromISO(res.publishedAt).diffNow('seconds').seconds <= 0

          if (
            (!checkIsPublish || res.book.status !== BookStatusEnum.PUBLISHED) &&
            !res.isPaid
          ) {
            restrictModal.show()
          } else if (
            !res.isPaid &&
            res.price > 0 &&
            isAuthenticated &&
            !myBook &&
            !user.isAdmin &&
            !res.passReadPromotion
          ) {
            let summaryCurrentCoin = 0
            if (userCoin.paidCoin) {
              summaryCurrentCoin += userCoin.paidCoin.value
            }

            if (userCoin.freeCoin) {
              summaryCurrentCoin += Math.floor(userCoin.freeCoin.value)
            }

            if (userCoin.thirdCoin) {
              summaryCurrentCoin += Math.floor(userCoin.thirdCoin.value)
            }

            if (user.emailEditable) {
              changeEmailModal.show({
                onSuccess: () => {
                  buyChapter(summaryCurrentCoin, res)
                },
                onClose: () => {
                  router.replace(`/story/${res.book.id}`)
                },
              })
            } else {
              buyChapter(summaryCurrentCoin, res)
            }
          } else {
            checkBookRate18Plus({
              bookId: res.book.id,
              isRateR: rateRIds.includes(res.book.rating.id),
              requireAgeVerify: res.book.requireAgeVerify,
              isOwner: myBook,
              isAdmin: user.isAdmin,
              level: ContentLevelEnum.CHAPTER,
              chapterId: res.id,
            })
          }

          handleCreateUserActionlog()
          canCreateUserActionLog.current =
            (res.price === 0 || res.isPaid) && !myBook && !user.isAdmin
          startTime.current = DateTime.now()
          previousChapterId.current = chapterId

          setShowData(
            user.isAdmin ||
              res.isPaid ||
              (res.price === 0 &&
                checkIsPublish &&
                res.book.status === BookStatusEnum.PUBLISHED) ||
              (myBook && res.status === ChapterStatusEnum.PUBLISHED) ||
              res.passReadPromotion
          )

          setReadingPageFooterDate({
            bookId,
            isWriter:
              res.book.penName.user?.writer?.status ===
              WriterStatusEnum.APPROVED,
            affiliation: res.book?.penName?.user?.writer?.affiliation,
            displayName: res.book.penName.user.displayName,
            penNameUserId: res.book.penName.user.id,
            penNameId: res.book.penName.id,
            penName: res.book.penName.firstPenName,
            penNameCover: res.book.penName.user.avatarImgPath,
            messageFormWriter: res.writerMessage,
            slug: res.book.penName.user.profilePageSlug,
            bookType: res.book.bookType,
            isAdmin: res.book.penName.user.isAdmin,
            email: res.book.penName.user.email,
            isPermanentBan: res.book.penName.user.isPermanentBan,
          })

          return res
        })
      },
      {
        enabled:
          (canFetchApi && isAuthenticated && !!userCoin.paidCoin) ||
          (canFetchApi && !isAuthenticated),
        onError: (err: any) => {
          if (err?.response?.errors?.length) {
            const [error] = err.response.errors
            if (error.statusCode === 400) {
              restrictModal.show()
            } else if (error.statusCode === 403) {
              loginAndGoBack.show()
            } else if (error.errorCode === 40011) {
              loginAndVerifyAgeCase()
            } else if (error.errorCode === 40012) {
              verifyAgeCase()
            } else if (error.errorCode === 40013) {
              waitVerifyAgeCase()
            }
          }
        },
      }
    )

    const { data: continueChapter } = useQuery(
      ['story', bookId, 'chapter', chapterId, 'readNow'],
      () =>
        userReadingClient
          .readNow(bookId, chapterId)
          .then(res => res)
          .catch(() => undefined),
      { enabled: isAuthenticated }
    )

    const { data: isBookmarked = false } = useQuery(
      ['story', bookId, 'chapter', chapterId, 'bookmarked'],
      () => bookmarkClient.isBookmarked(chapterId),
      {
        enabled: isAuthenticated,
      }
    )

    const { data: statusShelfData } = useGetBookShelfStatus(bookId)

    const { mutate: createUserActionLog } = useMutation(
      (userActionLog: UserActionLogType) =>
        userActionLogClient.createUserActionLog(userActionLog)
    )

    const { mutate: createUserReading } = useMutation(
      ({
        chapterPageId,
        readingPercentage,
      }: {
        chapterPageId: number
        readingPercentage?: number
      }) =>
        userReadingClient.createUserReading(chapterPageId, readingPercentage)
    )

    const continuePercentage = useMemo(() => {
      if (isFetching) return 0

      if (!isAuthenticated) {
        const guestReading = userReadingClient.getRawGuestReading()
        const book = guestReading.find(item => item.bookId === bookId)

        return book?.readingPercentage ?? 0
      }

      return continueChapter?.readingPercentage ?? 0
    }, [bookId, continueChapter, isAuthenticated, isFetching])

    function handleNovelContentWayPoint(inOut: boolean) {
      setDisableWayPoint(inOut)
    }

    function handlePercentageChange(percentage: number) {
      if (isAuthenticated) {
        createUserReading({
          chapterPageId: data?.chapterPages[0]?.id ?? 0,
          readingPercentage: percentage,
        })
        return
      }

      const items = userReadingClient.getRawGuestReading()
      const editingTarget = items.find(
        item => item.bookId === bookId && item.chapterId === chapterId
      )
      if (editingTarget) {
        editingTarget.readingPercentage = percentage
        editingTarget.updatedAt = DateTime.utc().toString()
        userReadingClient.replaceGuestReading(items)
      }
    }

    function handleCreateUserActionlog() {
      if (
        canCreateUserActionLog.current &&
        startTime.current &&
        previousChapterId.current
      ) {
        createUserActionLog({
          bookId,
          bookChapterId: previousChapterId.current,
          time: Math.abs(
            startTime.current.diffNow('milliseconds').milliseconds
          ),
          action: UserActionEnum.READ,
        })
      }
    }

    async function buyChapter(
      summaryCurrentCoin: number,
      res: BookChapterPreviewType
    ) {
      const now = DateTime.now().toUTC()

      const hasActiveCampaign =
        !!res.chapterCampaign &&
        DateTime.fromJSDate(new Date(res.chapterCampaign.startedAt)) <= now &&
        DateTime.fromJSDate(new Date(res.chapterCampaign.endedAt)) >= now
      const sellingPrice = hasActiveCampaign
        ? res.chapterCampaign.sellingPrice
        : res.price
      const buyChapterModalPayload = {
        chapterId: res.id,
        bookId,
        bookType: res.book.bookType,
        bookCategory: res.book.category,
        price: sellingPrice,
        bookName: res.book.title,
        chapterTitle: res.chapterTitle,
        chapterName: res.chapterName,
        onSuccess: () => {
          refetch()
        },
      }
      const analyticPayload = {
        value: sellingPrice,
        currency: 'THB',
        items: [
          {
            item_id: res.id,
            item_name: res.chapterName,
            item_category: res.book?.category?.id,
            item_variant: res.book?.bookType,
            price: sellingPrice,
          },
        ],
      }

      const bookReadPromotionDetail =
        await chapterClient.getBookReadFreePromotionDetail(bookId)
      if (
        bookReadPromotionDetail?.currentReadPromotion?.readPromotionType ===
          PromotionReadFreeEnum.READ_FREE_PER_DAY &&
        getTodayReadCount(now, bookReadPromotionDetail) >=
          (bookReadPromotionDetail?.currentReadPromotion?.readFreePerDay ??
            0) &&
        now.valueOf() <
          DateTime.fromISO(
            bookReadPromotionDetail?.currentReadPromotion?.endDate
          )
            .toUTC()
            .valueOf()
      ) {
        notEnoughReadFreeQuotaModal.show({
          quota:
            bookReadPromotionDetail?.currentReadPromotion?.readFreePerDay ?? 0,
          notEnoughCoin: summaryCurrentCoin < res.price,
          onClickBuy: () => {
            buyChapterModal.show(buyChapterModalPayload)

            gtagBeginCheckout(analyticPayload)
            fbqBeginCheckout(analyticPayload)
            ttqBeginCheckout(analyticPayload)
          },
          onClickRedeem: () => notEnoughCoinModal.show(),
          onClickClose: () => {
            router.replace(`/story/${bookId}`)
          },
        })
        return
      }

      if (summaryCurrentCoin >= sellingPrice) {
        buyChapterModal.show(buyChapterModalPayload)

        gtagBeginCheckout(analyticPayload)
        fbqBeginCheckout(analyticPayload)
        ttqBeginCheckout(analyticPayload)
        return
      }

      notEnoughCoinModal.show()
    }

    useEffect(() => {
      setThemeFromLocalStorage()
      return () => {
        setInitialData()
        handleCreateUserActionlog()
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const newProps = {
      theme,
      bookId,
      shareUrl: `${BASE_URL}/story/${bookId}/chapter/${chapterId}`,
      chapterId,
      continuePageId: continueChapter?.chapterPageId,
      data,
      readingPageFooterdata,
      isBookOnShelf: !!statusShelfData?.isBookOnShelf,
      showData,
      isBookmarked,
      metaData,
      fontSize,
      lineHeight,
      fontFamily,
      isLoading: isFetching,
      disableWayPoint,
      handleNovelContentWayPoint,
      handlePercentageChange,
      continuePercentage,
    }
    return <Component {...newProps} />
  }
  return WithChapterPage
}
